import { useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { SelectedVehicleContext } from '../../../contexts/vehicle/SelectedVehicleContext.ts';
import { useTerritoryConsolidatedGeography } from '../../../services/Geography.ts';
import { useRoadmap } from '../../../services/Roadmap.ts';
import { useTerritory } from '../../../services/Territory.ts';
import { DashboardMap } from '../../Map/MapComponent/DashboardMap.tsx';
import { CustomSpinner } from '../../ui/Spinner.tsx';
import { Kpi } from '../Kpi/Kpi.tsx';
import { KpiFallback } from '../Kpi/KpiFallback.tsx';
import { Mission } from '../Mission/Mission.tsx';
import { Roadmap } from '../Roadmap/Roadmap.tsx';
import { RoadmapFallback } from '../Roadmap/RoadmapFallback.tsx';

export function MapDashboard() {
  const { t } = useTranslation();
  const { selectedVehicle } = useContext(SelectedVehicleContext);
  const params = useParams() as { territorySlug: string };
  const {
    data: dispatchGeography,
    isLoading: geographyLoading,
    error: geographyError,
  } = useTerritoryConsolidatedGeography(params.territorySlug);
  const {
    data: territory,
    isLoading: territoryLoading,
    error: territoryError,
  } = useTerritory(params.territorySlug);
  const { data: roadmapData, isLoading: roadmapLoading } = useRoadmap(
    params.territorySlug,
    selectedVehicle?.id,
    selectedVehicle?.shuttle_provider_identifier,
  );

  if (geographyLoading || territoryLoading) {
    return <CustomSpinner class={'h-100 d-flex align-items-center justify-content-center'} />;
  }
  if (geographyError || territoryError) {
    if (geographyError) return <p className={'text-center mt-5'}>{t('errors.territory')}</p>;
    else return <p className={'text-center mt-5'}>{t('errors.dispatch-geography')}</p>;
  }

  return (
    <div className={'map-container'}>
      <div>
        {selectedVehicle?.id === undefined ? (
          <div className={'top-center-container d-flex flex-column'}>
            <ErrorBoundary resetKeys={[selectedVehicle?.id]} FallbackComponent={KpiFallback}>
              <Kpi />
            </ErrorBoundary>
          </div>
        ) : (
          <div className={'top-center-container row m-0 p-0 mh-100 d-flex justify-content-center'}>
            <ErrorBoundary resetKeys={[selectedVehicle?.id]} FallbackComponent={RoadmapFallback}>
              <Roadmap territory={territory} roadmapData={roadmapData} dataLoading={roadmapLoading} />
            </ErrorBoundary>
          </div>
        )}
      </div>
      {selectedVehicle && (
        <div className={'mission-container'}>
          <div className={'row m-0 p-0 py-3'}>
            <div>
              <p className={'card-title ms-2'}>
                <img src={'/theme/media/mission-icon.svg'} width="56" height="42" alt="Mission icon" />
                <span className={'ms-3'}>{t('mission.title')}</span>
              </p>
            </div>
          </div>
          <Mission />
        </div>
      )}
      {territory && dispatchGeography ? (
        <DashboardMap
          territory={territory}
          dispatchGeography={dispatchGeography}
          roadmapData={roadmapData}
        />
      ) : (
        <p className={'text-center mt-5'}>{t('errors.territory')}</p>
      )}
    </div>
  );
}
